import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WorkspaceContext = createContext(null);
const LS_KEY = "lwp_ws";

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem(LS_KEY) || null);
  const [loading, setLoading] = useState(true);

  const active = workspaces.find((w) => w.workspace_id === activeId) || workspaces[0] || null;

  const load = useCallback(async () => {
    try {
      const res = await api.get("/workspaces");
      const list = res.data.workspaces || [];
      setWorkspaces(list);
      const stored = localStorage.getItem(LS_KEY);
      const valid = list.find((w) => w.workspace_id === stored);
      const chosen = valid ? stored : list[0]?.workspace_id;
      if (chosen) {
        setActiveId(chosen);
        localStorage.setItem(LS_KEY, chosen);
      }
    } catch {
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user, load]);

  // Apply the active workspace accent color as a CSS variable.
  useEffect(() => {
    const accent = active?.accent_color || "#4da3ff";
    document.documentElement.style.setProperty("--ws-accent", accent);
  }, [active]);

  const switchWorkspace = (id) => {
    setActiveId(id);
    localStorage.setItem(LS_KEY, id);
    window.dispatchEvent(new Event("workspace-switched"));
  };

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, active, activeId: active?.workspace_id, loading, refresh: load, switchWorkspace }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
